import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './ServerErrorPage.scss';

interface ServerErrorPageProps {
  error?: Error;
  resetError?: () => void;
}

const ServerErrorPage: React.FC<ServerErrorPageProps> = ({ error, resetError }) => {
  const navigate = useNavigate();

  const handleRetry = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div className="server-error-page">
      <div className="container">
        <div className="server-error-content">
          <div className="error-number">500</div>
          <div className="error-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                fill="currentColor"
              />
            </svg>
          </div>
          <h1 className="error-title">Internal Server Error</h1>
          <p className="error-description">
            We're experiencing some technical difficulties. Our team has been notified and is working to resolve this issue as quickly as possible.
          </p>
          
          <div className="error-details">
            <div className="detail-item">
              <div className="detail-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2"/>
                  <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" strokeWidth="2"/>
                </svg>
              </div>
              <div className="detail-content">
                <h3>What happened?</h3>
                <p>Something went wrong on our end. This could be a temporary issue or a bug that needs fixing.</p>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="detail-content">
                <h3>What can you do?</h3>
                <p>Try refreshing the page or going back to the previous page. If the problem persists, please contact our support team.</p>
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12c-1 0-3-1-3-3s2-3 3-3 3 1 3 3-2 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12c1 0 3-1 3-3s-2-3-3-3-3 1-3 3 2 3 3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="detail-content">
                <h3>We're on it!</h3>
                <p>Our development team has been automatically notified and is working to fix this issue.</p>
              </div>
            </div>
          </div>

          {process.env.NODE_ENV === 'development' && error && (
            <details className="error-stack">
              <summary>Error Details (Development Only)</summary>
              <pre className="error-stack-content">
                {error.toString()}
                {error.stack}
              </pre>
            </details>
          )}

          <div className="error-actions">
            <button onClick={handleRetry} className="btn btn-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 4v6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M23 20v-6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4l-4.64 4.36A9 9 0 0 1 3.51 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Try Again
            </button>
            <button onClick={handleGoBack} className="btn btn-outline-primary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Go Back
            </button>
            <Link to="/" className="btn btn-outline-secondary">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="9,22 9,12 15,12 15,22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Go Home
            </Link>
          </div>

          <div className="support-section">
            <h3>Still having trouble?</h3>
            <p>If this error persists, please contact our support team with the following information:</p>
            <div className="support-info">
              <div className="info-item">
                <strong>Error Code:</strong> 500
              </div>
              <div className="info-item">
                <strong>Time:</strong> {new Date().toLocaleString()}
              </div>
              <div className="info-item">
                <strong>URL:</strong> {window.location.href}
              </div>
            </div>
            <Link to="/contact" className="btn btn-support">
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerErrorPage;
